import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If set, the authenticated user must have this role to enter. */
  requireRole?: 'admin' | 'agent' | 'user';
  /** Where to send unauthenticated users. Defaults to /admin/login. */
  loginPath?: string;
}

/**
 * Guards a route. Unauthenticated users -> loginPath. Authenticated but
 * wrong-role users -> their own role's home (or login if unclear).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole,
  loginPath = '/admin/login',
}) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginPath}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (requireRole && user?.role && user.role !== requireRole) {
    // Send user to the area that matches their actual role.
    const fallback =
      user.role === 'admin'
        ? '/admin'
        : user.role === 'agent'
        ? '/agent'
        : '/admin/login';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
